import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Clock, X } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { api } from '@/lib/api';
import { STATUS_CONFIG, type StatusBadgeVariant } from '@/lib/constants';
import type { PostStatus } from '@feedbackhub/types';
import { cn } from '@/lib/utils';

const RECENT_SEARCHES_KEY = 'feedbackhub_recent_searches';
const MAX_RECENT_SEARCHES = 5;
const RECENT_SEARCH_EXPIRY_DAYS = 30;
const MIN_SEARCH_LENGTH = 3;

interface RecentSearch {
  query: string;
  timestamp: number;
}

interface SearchResult {
  id: string;
  title: string;
  slug: string;
  status: PostStatus;
  board: {
    id: string;
    name: string;
    token: string;
  };
}

interface SearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchModal({ open, onOpenChange }: SearchModalProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(query, 300);

  // Load recent searches from localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        const searches: RecentSearch[] = JSON.parse(stored);
        const expiryTime = Date.now() - RECENT_SEARCH_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        const validSearches = searches.filter((s) => s.timestamp > expiryTime);
        setRecentSearches(validSearches.slice(0, MAX_RECENT_SEARCHES));
      }
    } catch {
      // Ignore localStorage errors
    }
  }, [open]);

  // Search when debounced query changes (minimum 3 characters)
  useEffect(() => {
    const trimmedQuery = debouncedQuery.trim();

    // Clear results when query is empty or below minimum length
    // This prevents stale results from remaining visible (#27)
    if (!trimmedQuery || trimmedQuery.length < MIN_SEARCH_LENGTH) {
      setIsLoading(false);
      setResults([]);
      setHasSearched(false);
      setSelectedIndex(0);
      setSearchError(null);
      return;
    }

    let cancelled = false;

    const searchPosts = async () => {
      setIsLoading(true);
      setSearchError(null);
      try {
        const response = await api.search.posts({ q: trimmedQuery, limit: 10 });
        if (!cancelled) {
          setResults(response.posts || []);
          setSelectedIndex(0);
          setHasSearched(true);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Search failed:', error);
          setResults([]);
          setSearchError('Search failed. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    searchPosts();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setHasSearched(false);
      setIsLoading(false);
      setSearchError(null);
      // Focus input after a short delay to ensure the modal is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [open]);

  const saveRecentSearch = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return;

    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      let searches: RecentSearch[] = stored ? JSON.parse(stored) : [];

      // Remove existing entry with same query
      searches = searches.filter((s) => s.query.toLowerCase() !== searchQuery.toLowerCase());

      // Add new search at the beginning
      searches.unshift({ query: searchQuery, timestamp: Date.now() });

      // Keep only the most recent searches
      searches = searches.slice(0, MAX_RECENT_SEARCHES);

      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
      setRecentSearches(searches);
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const clearRecentSearches = useCallback(() => {
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
      setRecentSearches([]);
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const navigateToResult = useCallback(
    (result: SearchResult) => {
      saveRecentSearch(query);
      onOpenChange(false);
      navigate(`/posts/${result.slug}`);
    },
    [query, navigate, onOpenChange, saveRecentSearch]
  );

  const handleRecentSearchClick = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery);
    },
    []
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const itemCount = results.length;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % itemCount || 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + itemCount) % itemCount || 0);
          break;
        case 'Enter':
          e.preventDefault();
          if (results.length > 0 && selectedIndex >= 0 && selectedIndex < results.length) {
            navigateToResult(results[selectedIndex]);
          }
          break;
      }
    },
    [results, selectedIndex, navigateToResult]
  );

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && results.length > 0) {
      const selectedElement = resultsRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, results.length]);

  const getStatusVariant = (status: PostStatus): StatusBadgeVariant => {
    return STATUS_CONFIG[status]?.variant || 'secondary';
  };

  const getStatusLabel = (status: PostStatus): string => {
    return STATUS_CONFIG[status]?.label || status;
  };

  const trimmedQuery = query.trim();
  const hasMinLength = trimmedQuery.length >= MIN_SEARCH_LENGTH;
  const showRecentSearches = !trimmedQuery && recentSearches.length > 0 && !hasSearched;
  const showMinLengthHint = trimmedQuery.length > 0 && !hasMinLength && !hasSearched;
  const showResults = results.length > 0;
  const showLoadingSpinner = isLoading;
  const showNoResults = hasMinLength && hasSearched && !isLoading && results.length === 0 && !searchError;
  const showEmptyState = !trimmedQuery && recentSearches.length === 0 && !hasSearched;
  const showError = !!searchError && !isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-xl overflow-hidden">
        <VisuallyHidden>
          <DialogTitle>Search posts</DialogTitle>
        </VisuallyHidden>
        {/* Search Input */}
        <div className="flex items-center border-b px-3">
          <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search posts..."
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-12 text-base"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="p-1 hover:bg-muted rounded"
              aria-label="Clear search"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Results Area */}
        <div className="max-h-[400px] overflow-y-auto" ref={resultsRef}>
          {/* Recent Searches */}
          {showRecentSearches && (
            <div className="p-2">
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Recent Searches
                </span>
                <button
                  type="button"
                  onClick={clearRecentSearches}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  aria-label="Clear recent searches"
                >
                  Clear
                </button>
              </div>
              {recentSearches.map((search, index) => (
                <button
                  key={`${search.query}-${index}`}
                  type="button"
                  onClick={() => handleRecentSearchClick(search.query)}
                  className="flex items-center gap-2 w-full px-2 py-2 hover:bg-muted rounded-md text-left"
                >
                  <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm truncate">{search.query}</span>
                </button>
              ))}
            </div>
          )}

          {/* Minimum Length Hint */}
          {showMinLengthHint && (
            <div className="p-8 text-center text-muted-foreground text-sm transition-opacity duration-150">
              Type at least {MIN_SEARCH_LENGTH} characters to search
            </div>
          )}

          {/* Loading Spinner - shown inline, doesn't replace content */}
          {showLoadingSpinner && !showResults && (
            <div className="p-8 text-center text-muted-foreground text-sm">
              <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            </div>
          )}

          {/* Search Results */}
          {showResults && (
            <div className="p-2 transition-opacity duration-150"
              style={{ opacity: isLoading ? 0.6 : 1 }}>
              {results.map((result, index) => (
                <button
                  key={result.id}
                  type="button"
                  data-index={index}
                  onClick={() => navigateToResult(result)}
                  className={cn(
                    'flex flex-col gap-1 w-full px-3 py-2 rounded-md text-left transition-colors',
                    selectedIndex === index ? 'bg-muted' : 'hover:bg-muted/50'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate flex-1">{result.title}</span>
                    <Badge variant={getStatusVariant(result.status)} className="text-[10px] flex-shrink-0">
                      {getStatusLabel(result.status)}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground truncate">
                    {result.board.name}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {showNoResults && (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No posts found for &quot;{query}&quot;
            </div>
          )}

          {/* Search Error */}
          {showError && (
            <div className="p-8 text-center text-sm">
              <p className="text-destructive">{searchError}</p>
              <button
                type="button"
                onClick={() => {
                  setSearchError(null);
                  // Re-trigger search by updating the query
                  const currentQuery = query;
                  setQuery('');
                  setTimeout(() => setQuery(currentQuery), 0);
                }}
                className="mt-2 text-muted-foreground hover:text-foreground underline"
              >
                Try again
              </button>
            </div>
          )}

          {/* Empty State */}
          {showEmptyState && (
            <div className="p-8 text-center text-muted-foreground text-sm">
              Start typing to search posts
            </div>
          )}
        </div>

        {/* Footer - Keyboard navigation hints */}
        <div
          className="border-t px-3 py-2 flex items-center gap-4 text-xs text-muted-foreground"
          role="status"
          aria-live="polite"
        >
          {/* Screen reader only instructions */}
          <span className="sr-only">
            Keyboard navigation: Use up and down arrow keys to navigate results,
            Enter to select, Escape to close the search dialog.
          </span>
          {/* Visual hints (hidden from screen readers) */}
          <div className="flex items-center gap-1" aria-hidden="true">
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">↑</kbd>
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">↓</kbd>
            <span>to navigate</span>
          </div>
          <div className="flex items-center gap-1" aria-hidden="true">
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">↵</kbd>
            <span>to select</span>
          </div>
          <div className="flex items-center gap-1" aria-hidden="true">
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">esc</kbd>
            <span>to close</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
